import { useState, useEffect, useRef } from 'react';
import { DEFAULT_PROGRAM } from '../constants/defaultProgram';
import { EXERCISE_DATABASE } from '../constants/exerciseDatabase';
import { s } from '../constants/styles';
import { getDayMeta, getDayExercises, getTrainingDayIndices, generateId } from '../utils/workout';
import ExercisePicker from './ExercisePicker';
import ExerciseForm from './ExerciseForm';
import VideoModal from './VideoModal';

export default function ProgramEditorScreen({ data, onSave, onClose }) {
  const trainingDays = getTrainingDayIndices(data.dayStructure);
  const [program, setProgram] = useState(() => {
    const p = {};
    trainingDays.forEach(idx => {
      p[idx] = getDayExercises(idx, data.customProgram, data.dayStructure).map(e => ({ ...e }));
    });
    return p;
  });
  const [editingEx, setEditingEx] = useState(null);
  const [form, setForm] = useState({});
  const [saved, setSaved] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [pickerDay, setPickerDay] = useState(null);
  const [videoFor, setVideoFor] = useState(null); // { videoId, name }
  const dragState = useRef({ active: false, dayIndex: null, fromIndex: null, lastY: 0 });
  const programRef = useRef(program);
  useEffect(() => { programRef.current = program; }, [program]);

  // Global touch handlers for drag
  useEffect(() => {
    function onTouchMove(e) {
      if (!dragState.current.active) return;
      e.preventDefault();
      const { dayIndex, fromIndex, lastY } = dragState.current;
      const currentY = e.touches[0].clientY;
      const ITEM_H = 62;
      if (Math.abs(currentY - lastY) > ITEM_H / 2) {
        const dir = currentY > lastY ? 1 : -1;
        const toIndex = fromIndex + dir;
        const arr = programRef.current[dayIndex];
        if (toIndex >= 0 && toIndex < arr.length) {
          const newArr = [...arr];
          [newArr[fromIndex], newArr[toIndex]] = [newArr[toIndex], newArr[fromIndex]];
          setProgram(p => ({ ...p, [dayIndex]: newArr }));
          dragState.current.fromIndex = toIndex;
          dragState.current.lastY = currentY;
          setDragging(d => d ? { ...d, exIndex: toIndex } : null);
        }
      }
    }
    function onTouchEnd() {
      dragState.current.active = false;
      setDragging(null);
    }
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  function startDrag(e, dayIdx, exIdx) {
    e.stopPropagation();
    dragState.current = { active: true, dayIndex: dayIdx, fromIndex: exIdx, lastY: e.touches[0].clientY };
    setDragging({ dayIndex: dayIdx, exIndex: exIdx });
    setEditingEx(null);
  }

  function openEdit(dayIndex, exIndex, isNew = false) {
    const ex = isNew
      ? { name: "", sets: 3, rLo: 8, rHi: 10, compound: false, inc: 2.5 }
      : { ...program[dayIndex][exIndex] };
    setForm(ex);
    setEditingEx({ dayIndex, exIndex, isNew });
  }

  function saveEdit() {
    if (!form.name?.trim()) return;
    const updated = { ...program };
    const exId = editingEx.isNew ? generateId() : program[editingEx.dayIndex][editingEx.exIndex].id;
    const exToSave = {
      ...form,
      id: exId,
      name: form.name.trim(),
      sets: Number(form.sets) || 3,
      rLo: Number(form.rLo) || 8,
      rHi: Number(form.rHi) || 10,
      compound: !!form.compound,
      inc: Number(form.inc) || 2.5,
      videoUrl: form.videoUrl?.trim() || undefined,
    };
    const dayArr = [...(updated[editingEx.dayIndex] || [])];
    if (editingEx.isNew) { dayArr.push(exToSave); } else { dayArr[editingEx.exIndex] = exToSave; }
    updated[editingEx.dayIndex] = dayArr;
    setProgram(updated);
    setEditingEx(null);
  }

  function removeEx(dayIndex, exIndex) {
    const updated = { ...program };
    updated[dayIndex] = program[dayIndex].filter((_, i) => i !== exIndex);
    setProgram(updated);
    setEditingEx(null);
  }

  function handleSave() {
    const cp = {};
    Object.entries(program).forEach(([idx, exArr]) => { cp[Number(idx)] = exArr; });
    onSave(cp);
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 800);
  }

  function resetToDefault() {
    const p = {};
    trainingDays.forEach(idx => { p[idx] = DEFAULT_PROGRAM[idx]?.exercises?.map(e => ({ ...e })) || []; });
    setProgram(p);
    setEditingEx(null);
  }

  return (
    <div className="slide-up" style={{ position: "fixed", inset: 0, zIndex: 70, background: "var(--bg-0)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", paddingTop: "max(12px, env(safe-area-inset-top))", flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <button onClick={onClose} style={{ ...s.btnG, padding: "10px 16px", fontSize: 14 }}>Cancel</button>
        <span style={{ fontSize: 16, fontWeight: 800 }}>Edit Program</span>
        <button onClick={handleSave} style={{ ...s.btnP, padding: "10px 16px", fontSize: 14 }}>
          {saved ? "✓ Saved" : "Save"}
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "16px 16px 100px" }}>
        <div style={{ fontSize: 12, color: "var(--text-3)", marginBottom: 16, padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}>
          Drag ≡ to reorder. Replacing or removing exercises preserves all past history.
        </div>

        {trainingDays.map(dayIdx => {
          const day = getDayMeta(dayIdx, data.dayStructure);
          const exList = program[dayIdx] || [];
          return (
            <div key={dayIdx} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: day.color, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                {day.day} — {day.name}
              </div>
              {exList.map((ex, exIdx) => {
                const isDragging = dragging?.dayIndex === dayIdx && dragging?.exIndex === exIdx;
                return (
                  <div key={ex.id || exIdx}>
                    <div style={{
                      display: "flex", alignItems: "center", padding: "11px 10px 11px 4px", borderRadius: 12, marginBottom: 4,
                      background: isDragging ? "rgba(79,143,255,0.1)" : "var(--bg-2)",
                      border: isDragging ? "1px solid rgba(79,143,255,0.25)" : "1px solid rgba(255,255,255,0.04)",
                      transform: isDragging ? "scale(1.01)" : "scale(1)",
                      transition: "background 0.1s, border 0.1s, transform 0.1s",
                    }}>
                      {/* Drag handle */}
                      <div
                        onTouchStart={e => startDrag(e, dayIdx, exIdx)}
                        style={{ padding: "6px 10px", color: "var(--text-3)", fontSize: 18, cursor: "grab", flexShrink: 0, touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}>
                        ≡
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ex.name}</div>
                        <div style={{ fontSize: 11, color: "var(--text-3)" }}>{ex.sets}x{ex.rLo}-{ex.rHi} · {ex.compound ? "Compound" : "Isolation"} · +{ex.inc}lb</div>
                      </div>
                      {/* Video play button */}
                      {(() => {
                        const dbEx = EXERCISE_DATABASE.find(e => e.id === ex.id) || EXERCISE_DATABASE.find(e => e.name === ex.name);
                        const vid = dbEx?.videoId || ex.videoId;
                        return vid ? (
                          <button onClick={() => setVideoFor({ videoId: vid, name: ex.name })} style={{
                            width: 30, height: 30, borderRadius: "50%", border: "none", cursor: "pointer",
                            background: `${day.color}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginLeft: 4,
                          }}>
                            <svg viewBox="0 0 24 24" width="12" height="12" fill={day.color}><path d="M8 5v14l11-7z"/></svg>
                          </button>
                        ) : null;
                      })()}
                      <button onClick={() => openEdit(dayIdx, exIdx)} style={{ ...s.btnG, padding: "6px 12px", fontSize: 12, marginLeft: 4, flexShrink: 0 }}>Edit</button>
                      <button onClick={() => removeEx(dayIdx, exIdx)} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "rgba(248,113,113,0.08)", color: "var(--red)", fontSize: 14, cursor: "pointer", marginLeft: 4, flexShrink: 0 }}>✕</button>
                    </div>
                    {editingEx?.dayIndex === dayIdx && editingEx?.exIndex === exIdx && !editingEx?.isNew && (
                      <ExerciseForm form={form} setForm={setForm} onSave={saveEdit} onCancel={() => setEditingEx(null)} dayColor={day.color} />
                    )}
                  </div>
                );
              })}
              {editingEx?.dayIndex === dayIdx && editingEx?.isNew && (
                <ExerciseForm form={form} setForm={setForm} onSave={saveEdit} onCancel={() => setEditingEx(null)} dayColor={day.color} isNew />
              )}
              <button onClick={() => setPickerDay(dayIdx)} style={{
                width: "100%", padding: "10px 0", borderRadius: 10, fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                background: "none", border: `1px dashed ${day.color}40`, color: day.color, cursor: "pointer", marginTop: 2,
              }}>+ Add Exercise</button>
            </div>
          );
        })}

        <button onClick={resetToDefault} style={{
          width: "100%", padding: "14px 0", borderRadius: 14, fontSize: 13, fontWeight: 700, border: "none", cursor: "pointer", fontFamily: "inherit",
          background: "var(--red-dim)", color: "var(--red)", marginTop: 8,
        }}>Reset to Default Program</button>
      </div>

      {pickerDay !== null && (
        <ExercisePicker
          dayColor={getDayMeta(pickerDay, data.dayStructure)?.color}
          onClose={() => setPickerDay(null)}
          onSelect={ex => {
            const newEx = { ...ex, id: ex.id.startsWith("ex-") ? ex.id : ex.id };
            setProgram(p => ({ ...p, [pickerDay]: [...(p[pickerDay] || []), newEx] }));
            setPickerDay(null);
          }}
        />
      )}

      {videoFor && (
        <VideoModal videoId={videoFor.videoId} exerciseName={videoFor.name} dayColor={getDayMeta(Object.keys(program)[0], data.dayStructure)?.color} onClose={() => setVideoFor(null)} />
      )}
    </div>
  );
}
